import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { normalizarValorOrdenacao } from '../../../../shared/helpers/ordenacao-tabela.helper';
import { RouterModule } from '@angular/router';
import { SelectionModel } from '@angular/cdk/collections';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { ConfirmModalComponent } from '../../../../shared/components/confirm-modal/confirm-modal.component';
import { EmptyListComponent } from '../../../../shared/components/empty-list/empty-list.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { UnidadeMedidaService } from '../../../../shared/services/apis/produto/unidade-medida.service';
import { UnidadeMedidaStruct } from '../../../../shared/services/structs/produto/unidade-medida.struct';
import { UtilService } from '../../../../shared/services/util.service';
import { LoadingBoxDefaultListComponent } from '../../../../shared/components/loading-box-default-list/loading-box-default-list.component';

@Component({
  selector: 'app-config-unidades-medida',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatCheckboxModule,
    MatMenuModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatTooltipModule,
    HeaderComponent,
    EmptyListComponent,
    RouterModule,
    LoadingBoxDefaultListComponent,
  ],
  templateUrl: './config-unidades-medida.component.html',
  styleUrl: './config-unidades-medida.component.css',
})
export class ConfigUnidadesMedidaComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Cadastros;

  public model: FormGroup;
  public _paginaAtual: number = 1;
  public _tamanhoPagina: number = 20;
  public _totalRegistros: number = 0;

  public _dataSource = new MatTableDataSource<UnidadeMedidaStruct>();
  public _isLoading: boolean = false;

  public displayedColumns: string[] = ['select', 'nome', 'actions'];

  public selection = new SelectionModel<UnidadeMedidaStruct>(true, []);

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this._dataSource.sort = sort;
    this._dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'nome':
          return normalizarValorOrdenacao(item.descricao);
        default:
          return normalizarValorOrdenacao((item as any)[property]);
      }
    };
  }

  constructor(
    private formBuilder: FormBuilder,
    private unidadeMedidaService: UnidadeMedidaService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private utilService: UtilService
  ) {}

  ngOnInit(): void {
    this.model = this.formBuilder.group({
      pesquisa: [''],
    });

    this.loadFilters();
    this.carregarUnidadesMedida();
  }

  ngOnDestroy(): void {
    this.utilService.salvarFiltro(this.moduloEnum, this.funcionalidadeEnum, this.model, [
      '/configuracoes/cadastros/unidades-medida/cadastrar',
      '/configuracoes/cadastros/unidades-medida/cadastrar/',
    ]);
  }

  private loadFilters(): void {
    const filtroSalvo = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
    if (filtroSalvo) {
      this.model.patchValue(filtroSalvo);
    }
  }

  public carregarUnidadesMedida(reset: boolean = false): void {
    this._isLoading = true;
    const pesquisa = this.model.get('pesquisa')?.value || '';

    this.unidadeMedidaService.getAll(pesquisa, this._paginaAtual, this._tamanhoPagina).subscribe({
      next: response => {
        this._isLoading = false;
        if (response.isError) {
          this.alertService.show(
            'Erro',
            response.errorDescription || 'Erro ao carregar unidades de medida',
            AlertType.error
          );
          return;
        }

        this._totalRegistros = response.totalRegistros;

        if (reset) {
          this._dataSource.data = response.listaUnidadeMedida;
          this.selection.clear();
        } else {
          this._dataSource.data = [...this._dataSource.data, ...response.listaUnidadeMedida];
        }
      },
      error: err => {
        this._isLoading = false;
        this.alertService.show(
          'Erro inesperado',
          err.message || 'Erro ao carregar unidades de medida',
          AlertType.error
        );
      },
    });
  }

  public proximaPagina(): void {
    this._paginaAtual++;
    this.carregarUnidadesMedida();
  }

  public clickPesquisar(): void {
    this._paginaAtual = 1;
    this._dataSource.data = [];
    this.selection.clear();
    this.carregarUnidadesMedida(true);
  }

  public getTooltipMessage(element: UnidadeMedidaStruct): string {
    if (element.isPadrao) {
      return 'Unidades padrão não podem ser alteradas ou excluídas.';
    }
    return '';
  }

  public isSelecaoBloqueada(row: UnidadeMedidaStruct): boolean {
    return !!row.isPadrao || !!row.possuiProdutos;
  }

  public getTooltipSelecaoBloqueada(row: UnidadeMedidaStruct): string {
    if (row.isPadrao) {
      return 'Esta unidade padrão não pode ser excluída.';
    }
    if (row.possuiProdutos) {
      return 'Esta unidade não pode ser excluída porque possui produtos vinculados.';
    }
    return '';
  }

  public getTooltipExcluir(row: UnidadeMedidaStruct): string {
    return this.getTooltipSelecaoBloqueada(row);
  }

  public isAllSelected() {
    const availableUnits = this._dataSource.data.filter(u => !this.isSelecaoBloqueada(u));
    const numSelected = this.selection.selected.length;
    const numRows = availableUnits.length;
    return numSelected === numRows && numRows > 0;
  }

  public isToggleAllDisabled(): boolean {
    return (
      this._dataSource.data.length === 0 ||
      this._dataSource.data.every(row => this.isSelecaoBloqueada(row))
    );
  }

  public toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }
    const availableUnits = this._dataSource.data.filter(u => !this.isSelecaoBloqueada(u));
    this.selection.select(...availableUnits);
  }

  public abrirModalExcluir(item?: UnidadeMedidaStruct): void {
    const isBulk = !item;
    const itemsToDelete = isBulk ? this.selection.selected : [item!];

    if (itemsToDelete.length === 0) return;

    const title = 'Confirmar exclusão';
    const description = 'Confirma a exclusão das unidades de medida selecionadas?';

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: title,
        description: description,
        buttonConfirm: 'Confirmar',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.confirm) {
        this._isLoading = true;

        if (isBulk) {
          const ids = itemsToDelete.map(i => i.idUnidadeMedida);
          this.unidadeMedidaService.deleteBulk(ids).subscribe({
            next: response => {
              this._isLoading = false;
              if (response.isError) {
                this.alertService.show(
                  'Atenção',
                  `Erro ao excluir unidades de medida: ${response.errorDescription}`,
                  AlertType.warning
                );
              } else {
                this.alertService.show(
                  'Sucesso',
                  'Unidades de medida excluídas com sucesso',
                  AlertType.success
                );
                this.selection.clear();
                this.clickPesquisar();
              }
            },
            error: err => {
              this._isLoading = false;
              this.alertService.show(
                'Erro inesperado',
                err.message || 'Erro ao excluir unidades de medida',
                AlertType.error
              );
            },
          });
        } else {
          this.unidadeMedidaService.delete(item!.idUnidadeMedida).subscribe({
            next: response => {
              this._isLoading = false;
              if (response.isError) {
                this.alertService.show(
                  'Erro',
                  response.errorDescription || 'Erro ao excluir unidade de medida',
                  AlertType.error
                );
              } else {
                this.alertService.show(
                  'Sucesso',
                  'Unidade de medida excluída com sucesso',
                  AlertType.success
                );
                this.clickPesquisar();
              }
            },
            error: err => {
              this._isLoading = false;
              this.alertService.show(
                'Erro inesperado',
                err.message || 'Erro ao excluir unidade de medida',
                AlertType.error
              );
            },
          });
        }
      }
    });
  }
}
